import { useMemo, type ReactElement, type ReactNode } from "react";
import { ConnectorUIProvider } from "@fastn-ai-team/connector-ui";
import { useAuth } from "./auth";
import { usePreviewTenantId } from "./preview-tenant";
import { apiFetch, apiFetchRaw, del, put } from "./api";
import { API_BASE } from "./utils";
import { queryClient } from "./query-client";
import { keys } from "@/hooks/keys";

/**
 * Feeds the dashboard's own `lib/*` implementations into the shared
 * `@fastn-ai-team/connector-ui` package. The package holds the connections
 * hooks; this bridge is the dashboard's copy of the auth / API-base / query
 * plumbing those hooks need — the admin console injects its own.
 *
 * Mounted twice: once high in App (so `previewTenantId` reads its default
 * `undefined`), and again inside WidgetsPage's `PreviewTenantContext.Provider`
 * so the previewed tenant flows through to the hooks its subtree renders.
 */
export function ConnectorUIBridge({ children }: { children: ReactNode }): ReactElement {
  const { userContext } = useAuth();
  const previewTenantId = usePreviewTenantId();

  // Stable identity so useConnectorUI() consumers don't re-render on every
  // bridge render. Only userContext / previewTenantId vary; the rest are
  // module-level singletons.
  const value = useMemo(
    () => ({
      apiBase: API_BASE,
      apiFetch,
      apiFetchRaw,
      del,
      put,
      userContext: userContext ?? undefined,
      previewTenantId,
      queryClient,
      keys,
      connectionScope: "saasPool" as const,
      connectionsEnabled: !!userContext?.orgId,
    }),
    [userContext, previewTenantId],
  );

  return <ConnectorUIProvider value={value}>{children}</ConnectorUIProvider>;
}
